import { Marker, type GeoJSONSource, type Map as MapLibreMap } from 'maplibre-gl'
import { STATUS_COLOURS, tokens } from '../../theme'

/**
 * Polygons, when the map is too far out to draw them.
 *
 * A cocoa plot is half a hectare. Across a sourcing region that is a pixel,
 * and five hundred of them are five hundred pixels scattered over a country
 * — the map shows that plots exist and nothing else. So below the zoom at
 * which a polygon has a readable shape, each one becomes a point and the
 * points are clustered: one bubble per group, carrying how many plots are
 * in it and whether any of them failed.
 *
 * The polygons themselves take over above that zoom. Both are the same set
 * of plots and the same filters; only the representation changes.
 *
 * The grouping is the server's (GET /runs/:id/plots/clusters), for the area
 * on screen: the browser never holds every plot of a large region, and the
 * answer is a screen's worth of cells however many plots there are.
 */

/**
 * Below this, plots are bubbles; at it and above, they are polygons.
 *
 * About 1:140k. A hectare is two or three pixels across there, and with its
 * outline a small coloured shape: each field can be told from its
 * neighbours, which is the point at which people expect to see fields
 * rather than counts. At z13 (where this used to be) the map already shows
 * village tracks and still showed bubbles. Further out, the outlines are a
 * scatter of single pixels and a grouped count says more.
 *
 * Where a screen at this zoom holds more plots than one response carries,
 * the bubbles stay until the next step in (see ResultsMap).
 */
export const CLUSTER_MAX_ZOOM = 12

export const POINTS_SOURCE = 'plot-points'
export const POINT_CIRCLE = 'plot-point'

const EMPTY: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] }

export function addClusterLayers(map: MapLibreMap): void {
  if (map.getSource(POINTS_SOURCE)) return

  // Grouped on the server (GET /runs/:id/plots/clusters): this source holds
  // only the cells with a single plot, drawn as a dot; the groups are DOM
  // bubbles, below.
  map.addSource(POINTS_SOURCE, { type: 'geojson', data: EMPTY })

  // A plot with no neighbours still has to be visible at this zoom, and a
  // one-plot bubble would overstate it. It gets a dot in its own colour.
  map.addLayer({
    id: POINT_CIRCLE,
    type: 'circle',
    source: POINTS_SOURCE,
    filter: ['!', ['has', 'point_count']],
    paint: {
      'circle-radius': 4,
      'circle-color': [
        'match', ['get', 'status'],
        'passed', STATUS_COLOURS.passed.stroke,
        'flagged', STATUS_COLOURS.flagged.stroke,
        'rejected', STATUS_COLOURS.rejected.stroke,
        'excluded', STATUS_COLOURS.excluded.stroke,
        STATUS_COLOURS.passed.stroke,
      ],
      'circle-stroke-width': 1.5,
      'circle-stroke-color': tokens.color.ink0,
    },
  })
}

interface Cluster {
  id: number
  count: number
  rejected: number
  flagged: number
  excluded: number
  lngLat: [number, number]
}

/** One cell of the server's grouping: where, how many, and how many failed. */
export interface PlotCell {
  lng: number
  lat: number
  count: number
  rejected: number
  flagged: number
  excluded: number
}

/** A single plot's status, from its one-plot cell. */
const statusOf = (c: PlotCell) =>
  c.rejected ? 'rejected' : c.flagged ? 'flagged' : c.excluded ? 'excluded' : 'passed'

/**
 * Draws the cells: a dot for a lone plot, a bubble for a group. The caller
 * passes none once the outlines take over.
 */
export function syncCells(
  map: MapLibreMap,
  markers: Map<string, Marker>,
  cells: PlotCell[],
  onZoomTo: (lngLat: [number, number]) => void,
): void {
  // The caller passes none once outlines take over.
  const showing = cells
  const source = map.getSource(POINTS_SOURCE)
  if (source && 'setData' in source) {
    (source as GeoJSONSource).setData({
      type: 'FeatureCollection',
      features: showing.filter((c) => c.count === 1).map((c) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [c.lng, c.lat] },
        properties: { status: statusOf(c) },
      })),
    })
  }

  // Keyed by what the bubble shows, so an unchanged group keeps its node
  // when the next answer arrives and only the ones that changed are redrawn.
  const live = new Set<string>()
  for (const c of showing) {
    if (c.count < 2) continue
    const key = `${c.lng},${c.lat},${c.count},${c.rejected},${c.flagged},${c.excluded}`
    live.add(key)
    if (markers.has(key)) continue
    const lngLat: [number, number] = [c.lng, c.lat]
    const cluster: Cluster = { id: 0, ...c, lngLat }
    markers.set(key, new Marker({ element: bubble(cluster, () => onZoomTo(lngLat)) }).setLngLat(lngLat).addTo(map))
  }
  for (const [key, marker] of markers) {
    if (!live.has(key)) {
      marker.remove()
      markers.delete(key)
    }
  }
}

export function removeClusterMarkers(markers: Map<string, Marker>): void {
  for (const marker of markers.values()) marker.remove()
  markers.clear()
}

/**
 * A ring showing what is inside, not a single colour standing for all of it.
 *
 * Colouring the whole bubble by its worst status made thirteen of twenty-one
 * groups red in a region where five per cent of polygons are rejected: true
 * of each group, and useless as a signal. The ring is the composition, so a
 * group that is mostly fine reads as mostly fine and one that is not stands
 * out. Exact numbers are in the tooltip, where they cannot be misread off a
 * few degrees of arc.
 */
function bubble(cluster: Cluster, onClick: () => void): HTMLElement {
  // 28px at a handful, 48px at a thousand: area reads as quantity, and a
  // linear radius would make the big ones swallow the map.
  const size = Math.round(28 + Math.min(20, Math.log10(Math.max(cluster.count, 1)) * 8))
  const label = cluster.count >= 1000
    ? `${(cluster.count / 1000).toFixed(1)}k`
    : String(cluster.count)

  /*
   * Two elements, and the outer one is never styled again.
   *
   * MapLibre positions a marker by writing `transform` on the element it was
   * given, every frame. Anything else that sets `transform` on that element
   * — a hover scale, say — overwrites the translation, and the bubble jumps
   * to the corner of the map and stays there. So the element handed over is
   * for position only, and everything the bubble does to itself happens on
   * the button inside it.
   */
  const anchor = document.createElement('div')
  Object.assign(anchor.style, {
    width: `${size}px`,
    height: `${size}px`,
    display: 'block',
  } satisfies Partial<CSSStyleDeclaration>)

  const el = document.createElement('button')
  el.type = 'button'
  el.setAttribute('data-testid', 'plot-cluster')
  el.setAttribute('data-count', String(cluster.count))
  const parts = [`${cluster.count} polygons`]
  if (cluster.rejected) parts.push(`${cluster.rejected} rejected`)
  if (cluster.flagged) parts.push(`${cluster.flagged} flagged`)
  if (cluster.excluded) parts.push(`${cluster.excluded} excluded`)
  parts.push('click to zoom in')
  el.title = parts.join(' · ')
  el.setAttribute('aria-label', parts.join(', '))

  Object.assign(el.style, {
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    border: 'none',
    padding: '0',
    background: ring(cluster),
    display: 'grid',
    placeItems: 'center',
    cursor: 'pointer',
    boxShadow: tokens.shadow[2],
    transition: `transform ${tokens.motion.dur.fast}ms ${tokens.motion.easeStd}`,
  } satisfies Partial<CSSStyleDeclaration>)
  anchor.appendChild(el)

  const face = document.createElement('span')
  face.textContent = label
  Object.assign(face.style, {
    width: `${size - 9}px`,
    height: `${size - 9}px`,
    borderRadius: '50%',
    backgroundColor: tokens.color.ink0,
    color: tokens.color.ink700,
    font: `500 ${size >= 40 ? 13 : 12}px ${tokens.font.sans}`,
    fontVariantNumeric: 'tabular-nums',
    display: 'grid',
    placeItems: 'center',
  } satisfies Partial<CSSStyleDeclaration>)
  el.appendChild(face)

  el.addEventListener('mouseenter', () => { el.style.transform = 'scale(1.08)' })
  el.addEventListener('mouseleave', () => { el.style.transform = 'none' })
  el.addEventListener('click', (e) => {
    e.stopPropagation()
    onClick()
  })
  return anchor
}

/**
 * The ring as a conic gradient, rejected first and clockwise from the top.
 *
 * Any failing status that is present gets at least a few degrees whatever
 * its share: one rejected polygon in ninety-three is four degrees of arc and
 * invisible, and the whole reason to look at this group is that it is there.
 * The distortion is at most a few per cent of the circle and only ever
 * overstates a failure, never hides one.
 */
function ring(cluster: Cluster): string {
  const total = Math.max(cluster.count, 1)
  const MIN = 14
  const slice = (n: number) => (n === 0 ? 0 : Math.max(MIN, (n / total) * 360))
  const rejected = slice(cluster.rejected)
  const flagged = slice(cluster.flagged)
  const excluded = slice(cluster.excluded)
  const passed = Math.max(0, 360 - rejected - flagged - excluded)

  const stops: string[] = []
  let at = 0
  const add = (colour: string, degrees: number) => {
    if (degrees <= 0) return
    stops.push(`${colour} ${at}deg ${at + degrees}deg`)
    at += degrees
  }
  add(STATUS_COLOURS.rejected.stroke, rejected)
  add(STATUS_COLOURS.flagged.stroke, flagged)
  add(STATUS_COLOURS.excluded.stroke, excluded)
  add(STATUS_COLOURS.passed.stroke, passed)
  return `conic-gradient(from -90deg, ${stops.join(', ')})`
}
